/**
 * blog-v2-listing.js
 *
 * Progressive enhancement for the Blog V2 blog directory section.
 * Adds client-side search, tag/tab filtering, and AJAX load-more
 * using the Shopify Section Rendering API.
 *
 * Design constraints
 * ------------------
 * - No-JS: all initial article cards are rendered by Liquid and linked
 *   normally. This file only adds interactive enhancements on top.
 * - Load more: fetches the next Liquid-paginated page via the Section
 *   Rendering API (?sections=<sectionId>&page=N), appends the new
 *   article cards, and chains to the next page URL from the response.
 * - Filtering: search and tag filtering operate over all cards currently
 *   in the DOM. The first non-empty search query (or a real tab
 *   selection) triggers a bounded, one-time hydration that fetches the
 *   remaining paginated pages via the Section Rendering API and appends
 *   their (de-duplicated) cards, so filtering then matches across every
 *   published article in the blog. This is title/tag matching across all
 *   pages, not Shopify full-text search over article bodies.
 * - No build step: direct deployable asset (not in the Vite graph).
 *
 * SOURCE: frontend/web/blog/scripts/blog-v2-listing.js
 * MIRROR: assets/blog-v2-listing.js  (must be byte-identical)
 */

/* ---------------------------------------------------------------
   Constants
   --------------------------------------------------------------- */
const SECTION_CLASS = 'bv2-listing';
const ARTICLE_SELECTOR = '[data-bv2l-article]';
const ARTICLE_TAG_ATTR = 'data-bv2l-tags';
const ARTICLE_TITLE_ATTR = 'data-bv2l-title';
const TAB_SELECTOR = '[data-bv2l-tab]';
const TAB_TAG_ATTR = 'data-bv2l-tab';
const SEARCH_SELECTOR = '[data-bv2l-search]';
const NO_RESULTS_SELECTOR = '[data-bv2l-no-results]';
const CURRENT_TAG_ATTR = 'data-bv2l-current-tag';
const LOAD_MORE_SELECTOR = '[data-bv2l-load-more]';
const LOAD_MORE_WRAP_SELECTOR = '[data-bv2l-browse-mode]';
const LEAD_CLASS = 'bv2-listing__article-card--lead';
const HIDDEN_CLASS = 'bv2-listing__article-card--hidden';
const ACTIVE_CLASS = 'is-active';

/**
 * Hard cap on the number of extra pages search/tab hydration will fetch
 * via the Section Rendering API. Prevents unbounded network work on very
 * large blogs. Load-more (one page per click) is not affected by this cap.
 */
const MAX_HYDRATION_PAGES = 20;

/* ---------------------------------------------------------------
   State
   --------------------------------------------------------------- */
let activeTag = '';
let searchQuery = '';

/* ---------------------------------------------------------------
   Helpers
   --------------------------------------------------------------- */

/**
 * Normalise a string for comparison: lowercase, trim whitespace.
 * @param {string} s
 * @returns {string}
 */
function norm(s) {
  return (s || '').toLowerCase().trim();
}

/**
 * Derive the active tag from the current URL, matching it back to a rendered
 * tab so we recover the tag's DISPLAY form (e.g. "Buying guide") rather than
 * its handle (e.g. "buying-guide"). cardMatches compares display forms.
 * Returns '' for the unfiltered blog URL.
 * @param {HTMLElement} section
 * @returns {string}
 */
function tagFromLocation(section) {
  const match = window.location.pathname.match(/\/tagged\/([^/?#]+)/);
  if (!match) return '';
  const handle = decodeURIComponent(match[1]).toLowerCase();

  const tabs = Array.from(section.querySelectorAll(TAB_SELECTOR));
  const hit = tabs.find((t) => {
    const href = t.getAttribute('href') || '';
    const m = href.match(/\/tagged\/([^/?#]+)/);
    return m && decodeURIComponent(m[1]).toLowerCase() === handle;
  });
  return hit ? (hit.getAttribute(TAB_TAG_ATTR) || '') : '';
}

/**
 * Derive a stable identity key for an article card so load-more and search
 * hydration never append the same article twice. Prefers the card's anchor
 * href (article URL); falls back to the normalised title.
 * @param {HTMLElement} card
 * @returns {string}
 */
function cardKey(card) {
  const link = card.querySelector('a[href]');
  const href = link && link.getAttribute('href');
  if (href) {
    // Strip query/hash so the same article at different query strings dedupes.
    return href.split('?')[0].split('#')[0];
  }
  return norm(card.getAttribute(ARTICLE_TITLE_ATTR) || '');
}

/**
 * Build the set of article keys already present in a section's grid.
 * @param {HTMLElement} section
 * @returns {Set<string>}
 */
function collectSeenKeys(section) {
  const seen = new Set();
  section.querySelectorAll(ARTICLE_SELECTOR).forEach((card) => {
    seen.add(cardKey(card));
  });
  return seen;
}

/**
 * Return whether a single article card matches both active tag and
 * search query.
 * @param {HTMLElement} card
 * @returns {boolean}
 */
function cardMatches(card) {
  const tagsRaw = card.getAttribute(ARTICLE_TAG_ATTR) || '';
  const title = norm(card.getAttribute(ARTICLE_TITLE_ATTR) || '');
  const tags = tagsRaw.split(',').map(norm).filter(Boolean);

  const tagOk = !activeTag || tags.includes(norm(activeTag));
  const searchOk = !searchQuery || title.includes(norm(searchQuery)) ||
                   tags.some((t) => t.includes(norm(searchQuery)));
  return tagOk && searchOk;
}

/* ---------------------------------------------------------------
   Filter + show/hide
   --------------------------------------------------------------- */

/**
 * Apply active tag and search state to all article cards in the DOM.
 * Shows all matching cards, hides non-matching. Load-more button
 * visibility is managed separately by Liquid (initial) and
 * fetchNextPage (after each fetch).
 * @param {HTMLElement} section
 */
function applyFilter(section) {
  const cards = Array.from(section.querySelectorAll(ARTICLE_SELECTOR));
  const noResults = section.querySelector(NO_RESULTS_SELECTOR);

  const matching = cards.filter(cardMatches);
  const notMatching = cards.filter((c) => !cardMatches(c));

  notMatching.forEach((c) => c.classList.add(HIDDEN_CLASS));
  matching.forEach((c) => c.classList.remove(HIDDEN_CLASS));

  if (noResults) {
    noResults.classList.toggle('is-visible', matching.length === 0);
  }
}

/* ---------------------------------------------------------------
   Tab / category filtering
   --------------------------------------------------------------- */

/**
 * Reflect the given tag in the tab bar's active class and aria-pressed.
 * Matches on the tag's display form, which is what data-bv2l-tab carries.
 * @param {HTMLElement} section
 * @param {string} tag
 */
function syncTabState(section, tag) {
  const tabs = Array.from(section.querySelectorAll(TAB_SELECTOR));
  tabs.forEach((t) => {
    const isActive = norm(t.getAttribute(TAB_TAG_ATTR) || '') === norm(tag);
    t.classList.toggle(ACTIVE_CLASS, isActive);
    t.setAttribute('aria-pressed', String(isActive));
  });
}

/**
 * Apply a tag selection to state, tab UI and the article grid.
 * Does not touch history; callers decide whether the URL should change
 * (a click pushes, a popstate must not).
 * @param {HTMLElement} section
 * @param {string} tag
 */
function selectTag(section, tag) {
  activeTag = tag;
  syncTabState(section, tag);
  applyFilter(section);

  // Selecting a real tag filters across all published articles, so
  // hydrate the remaining pages once. applyFilter runs again when it
  // resolves. The "All" tab (empty tag) does not trigger hydration.
  if (tag) hydrateAllPages(section);
}

/**
 * Wire tab links to update the active tag and re-filter in place.
 *
 * Tabs are real <a href="/blogs/<blog>/tagged/<handle>"> links so they stay
 * crawlable, shareable and functional without JS. Here we preventDefault and
 * filter client-side for the instant feel, then pushState so the URL still
 * reflects the visible filter and remains copy-pasteable.
 * @param {HTMLElement} section
 */
function initTabs(section) {
  const tabs = Array.from(section.querySelectorAll(TAB_SELECTOR));
  if (!tabs.length) return;

  // When the server already filtered this response (a direct /tagged/ visit),
  // the DOM holds only that tag's articles. Page 1 of the unfiltered set is
  // never re-fetched by hydration, so filtering client-side from here would
  // show a corrupted mix. In that state let tabs navigate normally.
  const serverFiltered = Boolean(section.getAttribute(CURRENT_TAG_ATTR));

  tabs.forEach((tab) => {
    tab.addEventListener('click', (event) => {
      // Honour modified clicks (new tab/window) and let the browser navigate.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey ||
          event.button !== 0) {
        return;
      }
      if (serverFiltered) return;
      event.preventDefault();

      const tag = tab.getAttribute(TAB_TAG_ATTR) || '';
      selectTag(section, tag);

      const href = tab.getAttribute('href');
      if (href && window.history && typeof window.history.pushState === 'function') {
        window.history.pushState({ bv2lTag: tag }, '', href);
      }
    });
  });

  // Back/Forward must re-sync the filter with the URL the browser restored,
  // otherwise the grid silently contradicts the address bar. Only meaningful
  // when we pushed state in the first place.
  if (!serverFiltered) {
    window.addEventListener('popstate', () => {
      selectTag(section, tagFromLocation(section));
    });
  }
}

/* ---------------------------------------------------------------
   Search filtering
   --------------------------------------------------------------- */

/**
 * Wire the search input to update the query and re-filter.
 * Uses input event for live filtering; debounced at 150ms.
 * @param {HTMLElement} section
 */
function initSearch(section) {
  const input = section.querySelector(SEARCH_SELECTOR);
  if (!input) return;

  let timer;
  input.addEventListener('input', () => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      searchQuery = norm(input.value);
      applyFilter(section);

      // The first non-empty query hydrates the remaining paginated pages
      // so search matches across every published article, not just the
      // cards initially rendered. hydrateAllPages is idempotent and
      // re-applies the filter when it resolves.
      if (searchQuery) hydrateAllPages(section);
    }, 150);
  });
}

/* ---------------------------------------------------------------
   Section Rendering API — shared page fetch
   --------------------------------------------------------------- */

/**
 * Fetch one paginated page of the listing section via the Shopify Section
 * Rendering API and append any not-yet-seen article cards to the grid.
 *
 * This is the single source of truth for both load-more (one page per
 * click) and search/tab hydration (all remaining pages). De-duplication
 * is keyed on cardKey() so the two paths can never append the same
 * article twice.
 *
 * @param {HTMLElement} section
 * @param {string} url            The paginated page URL to fetch.
 * @param {Set<string>} seenKeys  Article keys already in the DOM.
 * @returns {Promise<{appended: number, nextUrl: string}>}
 */
async function fetchSectionPage(section, url, seenKeys) {
  const sectionId = section.dataset.bv2lSectionId;
  if (!url || !sectionId) return { appended: 0, nextUrl: '' };

  const separator = url.includes('?') ? '&' : '?';
  const fetchUrl = `${url}${separator}sections=${encodeURIComponent(sectionId)}`;
  const res = await fetch(fetchUrl);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);

  const data = await res.json();
  const html = data[sectionId];
  // Section Rendering API returns null for sections that fail to render.
  if (!html) throw new Error('Section not found in response');

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  const grid = section.querySelector('.bv2-listing__articles');
  let appended = 0;
  Array.from(doc.querySelectorAll(ARTICLE_SELECTOR)).forEach((card) => {
    const key = cardKey(card);
    if (seenKeys.has(key)) return;
    seenKeys.add(key);
    // Strip lead class — the page 1 lead is already set.
    card.classList.remove(LEAD_CLASS);
    grid.appendChild(card);
    appended += 1;
  });

  const fetchedBtn = doc.querySelector(LOAD_MORE_SELECTOR);
  const nextUrl = (fetchedBtn && fetchedBtn.dataset.bv2lNextUrl) || '';
  return { appended, nextUrl };
}

/* ---------------------------------------------------------------
   Load more — Section Rendering API
   --------------------------------------------------------------- */

/**
 * Fetch the next paginated page and append its cards. Updates the
 * button's data-bv2l-next-url for chaining, or hides the button when no
 * further pages exist.
 * @param {HTMLElement} section
 * @param {HTMLButtonElement} btn
 */
async function fetchNextPage(section, btn) {
  const nextUrl = btn.dataset.bv2lNextUrl;
  if (!nextUrl) return;

  btn.disabled = true;
  btn.setAttribute('aria-busy', 'true');

  try {
    const seenKeys = collectSeenKeys(section);
    const { nextUrl: newNextUrl } = await fetchSectionPage(section, nextUrl, seenKeys);
    if (newNextUrl) {
      btn.dataset.bv2lNextUrl = newNextUrl;
    } else {
      btn.hidden = true;
    }
    applyFilter(section);
  } catch (err) {
    console.error('[bv2-listing] load more failed:', err);
  } finally {
    btn.disabled = false;
    btn.removeAttribute('aria-busy');
  }
}

/**
 * Wire the load-more button to fetch the next page via the Section
 * Rendering API.
 * @param {HTMLElement} section
 */
function initLoadMore(section) {
  const btn = section.querySelector(LOAD_MORE_SELECTOR);
  if (!btn) return;

  btn.addEventListener('click', () => {
    fetchNextPage(section, btn);
  });
}

/* ---------------------------------------------------------------
   Search / tab hydration — Section Rendering API
   --------------------------------------------------------------- */

/**
 * Resolve the first paginated page URL to hydrate from.
 * - load_more mode: the load-more button's data-bv2l-next-url.
 * - pagination mode (or no button): page 2 of the blog's paginate base,
 *   read from data-bv2l-paginate-base on the load-more wrap.
 * Returns '' when there is nothing further to hydrate.
 * @param {HTMLElement} section
 * @returns {string}
 */
function resolveHydrationStartUrl(section) {
  const btn = section.querySelector(LOAD_MORE_SELECTOR);
  if (btn && btn.dataset.bv2lNextUrl) return btn.dataset.bv2lNextUrl;

  const wrap = section.querySelector(LOAD_MORE_WRAP_SELECTOR);
  const base = wrap && wrap.dataset.bv2lPaginateBase;
  if (!base) return '';
  const separator = base.includes('?') ? '&' : '?';
  return `${base}${separator}page=2`;
}

/**
 * Fetch every remaining paginated page for a section so that client-side
 * search and tab filtering can match across the full published article
 * set, not just the cards initially rendered.
 *
 * Runs at most once per section (idempotent), is bounded by
 * MAX_HYDRATION_PAGES, and serialises so overlapping input events reuse a
 * single in-flight promise. On any fetch failure it stops gracefully,
 * leaving already-loaded cards filterable.
 *
 * @param {HTMLElement} section
 * @returns {Promise<void>}
 */
function hydrateAllPages(section) {
  if (section.__bv2lHydrated) return Promise.resolve();
  if (section.__bv2lHydrating) return section.__bv2lHydrating;

  const run = (async () => {
    try {
      const seenKeys = collectSeenKeys(section);
      let url = resolveHydrationStartUrl(section);
      let pages = 0;

      while (url && pages < MAX_HYDRATION_PAGES) {
        const { nextUrl } = await fetchSectionPage(section, url, seenKeys);
        pages += 1;
        url = nextUrl;
      }

      // All cards are now in the DOM; the load-more button is redundant.
      const btn = section.querySelector(LOAD_MORE_SELECTOR);
      if (btn) btn.hidden = true;

      section.__bv2lHydrated = true;
    } catch (err) {
      console.error('[bv2-listing] search hydration failed:', err);
    } finally {
      section.__bv2lHydrating = null;
      applyFilter(section);
    }
  })();

  section.__bv2lHydrating = run;
  return run;
}

/* ---------------------------------------------------------------
   Boot
   --------------------------------------------------------------- */

/**
 * Initialise all Blog V2 listing sections on the page.
 * Safe to call multiple times; sections are initialised only once.
 */
function init() {
  const sections = Array.from(document.querySelectorAll(`.${SECTION_CLASS}`));
  sections.forEach((section) => {
    if (section.__bv2lInit) return;
    section.__bv2lInit = true;

    // Seed from Liquid's current_tags (server-rendered) so a direct visit to a
    // /tagged/ URL agrees with the server-filtered DOM. Falls back to parsing
    // the URL. Resetting to '' here would make the tab bar contradict the grid.
    activeTag = section.getAttribute(CURRENT_TAG_ATTR) || '';
    searchQuery = '';

    initTabs(section);
    if (!activeTag) activeTag = tagFromLocation(section);
    syncTabState(section, activeTag);
    applyFilter(section);
    initSearch(section);
    initLoadMore(section);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
